// Admin › Devices › one device: where it fetches map tiles. Every Scout and Gateway fetches
// direct from the providers until an admin moves that one device onto the tile front door
// (docs/planning/TILE_FRONT_DOOR_PLAN.md §3.5), so the front door is tried on a chosen boat
// before any other. The device picks the change up with its next tile pass; builds older
// than the front door ignore it.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Square3Stack3DIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';

type Route = 'direct' | 'front_door';

const OPTIONS: { route: Route; label: string; note: string }[] = [
  { route: 'direct', label: 'Direct', note: 'From the providers, as every boat today' },
  { route: 'front_door', label: 'Front door (testing)', note: 'Through tiles.vortexnav.com, counted per tile, direct as fallback' },
];

export function TileRouteCard({ deviceId, metadata }: { deviceId: string; metadata?: Record<string, unknown> | null }) {
  const queryClient = useQueryClient();
  const current: Route = metadata?.tile_route === 'front_door' ? 'front_door' : 'direct';
  const change = useMutation({
    mutationFn: (route: Route) => api.adminSetTileRoute(deviceId, route),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'device', deviceId] }),
  });
  const shown = change.isPending ? change.variables ?? current : current;

  return (
    <section className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Square3Stack3DIcon className="w-5 h-5 text-cyan-400" />
          <h3 className="text-[16px] font-semibold text-white">Map tiles</h3>
        </div>
        <div className="flex gap-2" role="group" aria-label="Map tile route">
          {OPTIONS.map((o) => (
            <button
              key={o.route}
              type="button"
              aria-pressed={shown === o.route}
              disabled={change.isPending}
              onClick={() => o.route !== current && change.mutate(o.route)}
              className={`px-3 py-1.5 rounded-lg text-[14px] font-semibold border ${shown === o.route
                ? 'bg-cyan-500 text-slate-900 border-cyan-500'
                : 'text-slate-200 border-slate-600 hover:border-slate-400'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[14px] text-slate-400 mt-2">
        {OPTIONS.find((o) => o.route === shown)!.note}. Takes effect at the device&apos;s next tile pass; only builds with the front door read it.
      </p>
      {change.isError && <p className="text-[14px] text-red-400 mt-1">Could not change the route. Try again.</p>}
    </section>
  );
}
